import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Button,
  Input,
  Table,
} from "reactstrap";
import Select from "react-select";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getLeaveTypes } from "../../components/Api/GmsLeaveType";
import { processEmployeeCredits, getEmployeeCreditHistory, hasAutomaticCreditingStarted } from "../../components/Api/GmsAnnualLeaveCreditHistory";
import { getLeaveBalanceHistory, saveLeaveBalanceHistory } from "../../components/Api/GmsLeaveBalanceHistory";
import { getEmployeeLeaves } from "../../components/Api/GmsEmployeeLeave";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const EmployeeLeaveCreditPage = () => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [creditHistory, setCreditHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [hasNoCreditHistory, setHasNoCreditHistory] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEmployeeLeaveCredit');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsEmployeeLeaveCredit');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsEmployeeLeaveCredit');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsEmployeeLeaveCredit');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadLeaveTypes();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness && !selectedBranch) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  useEffect(() => {
    if (selectedBusiness) {
      loadBranches(selectedBusiness.value);
    } else {
      setBranches([]);
      setSelectedBranch(null);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (selectedBusiness || selectedBranch) {
      filterEmployees();
    } else {
      loadEmployees();
    }
  }, [selectedBusiness, selectedBranch]);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      const formattedBusinesses = data.map(bus => ({
        value: bus.business_Id,
        label: bus.business_NameEn || bus.business_Name
      }));
      setBusinesses(formattedBusinesses);
    } catch (error) {
      console.error('Error loading businesses:', error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  };

  const loadBranches = async (companyId) => {
    try {
      const data = await getBranches({ Branch_Buisness_Id: companyId, branch_Active: true });
      const formattedBranches = data.map(branch => ({
        value: branch.Branch_Id || branch.branch_Id,
        label: branch.Branch_NameEn || branch.branch_NameEn || branch.Branch_Name || branch.branch_Name
      }));
      setBranches(formattedBranches);
    } catch (error) {
      console.error('Error loading branches:', error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      if (selectedBusiness) {
        filter.Employee_Business_Id = selectedBusiness.value;
      } else if (session.businessId) {
        // Use session business if no selection
        filter.Employee_Business_Id = session.businessId;
      }
      if (selectedBranch) {
        filter.Employee_Branch_Id = selectedBranch.value;
      } else if (session.branchId && session.businessId) {
        // Use session branch if no selection
        filter.Employee_Branch_Id = session.branchId;
      }

      const data = await getEmployees(filter);
      const formattedEmployees = data.map(emp => ({
        value: emp.employee_Id,
        label: emp.employee_NameEn || emp.employee_Name,
        genderId: emp.employee_Gender_Id,
        employeeCode: emp.employee_Code,
        joinDate: emp.employee_JoinDate
      }));
      console.log('Total employees loaded:', formattedEmployees.length);
      setEmployees(formattedEmployees);
      setFilteredEmployees(formattedEmployees);
    } catch (error) {
      console.error('Error loading employees:', error);
      Swal.fire("Error", "Failed to load employees", "error");
    }
  };

  const filterEmployees = async () => {
    await loadEmployees();
  };

  // Manual search function for employee search
  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = selectedBusiness?.value || session.businessId || null;
      const branchId = selectedBranch?.value || (session.branchId && session.businessId ? session.branchId : null);
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);

      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: `${employee.employee_NameEn || 'N/A'} — ${employee.emp_No || employee.Emp_No || 'N/A'}`,
        genderId: employee.employee_Gender_Id,
        employeeCode: employee.employee_Code,
        joinDate: employee.employee_JoinDate,
        employee: employee
      }));

      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const handleBusinessChange = (selectedOption) => {
    setSelectedBusiness(selectedOption);
    setSelectedBranch(null);
    setSelectedEmployee(null);
    setSelectedEmployeeDetails(null);
    setLeaveBalances([]);
    setCreditHistory([]);
  };

  const handleBranchChange = (selectedOption) => {
    setSelectedBranch(selectedOption);
    setSelectedEmployee(null);
    setSelectedEmployeeDetails(null);
    setLeaveBalances([]);
    setCreditHistory([]);
  };

  const loadLeaveTypes = async () => {
    try {
      const data = await getLeaveTypes({});
      const formattedTypes = data.map(lt => ({
        value: lt.leaveType_Id,
        label: lt.leaveType_NameEn || lt.leaveType_Name,
        fullPaidDays: lt.leaveType_FullPaidDays,
        halfPaidDays: lt.leaveType_HalfPaidDays,
        unpaidDays: lt.leaveType_UnPaidDays
      }));
      setLeaveTypes(formattedTypes);
    } catch (error) {
      console.error('Error loading leave types:', error);
      Swal.fire("Error", "Failed to load leave types", "error");
    }
  };

  const initializeBalances = async (employeeId) => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;

    try {
      console.log('Starting initializeBalances for employee:', employeeId);

      // Get employee details for gender check
      const employeeData = await getEmployees({ employee_Id: employeeId });
      if (!employeeData || employeeData.length === 0) {
        throw new Error('Employee not found');
      }
      const employeeGenderId = employeeData[0].employee_Gender_Id;
      const joinDate = new Date(employeeData[0].employee_JoinDate);

      // Get credit history FIRST to check if employee actually has credits
      // This ensures we calculate from credits even if hasNoCreditHistory state is still true
      const creditHistoryCheck = await getEmployeeCreditHistory(employeeId);
      const hasActualCreditHistory = creditHistoryCheck && creditHistoryCheck.length > 0;

      // IMPORTANT: Always build full list of leave types, regardless of hasNoCreditHistory state
      // The hasNoCreditHistory state is only for UI display, not for determining what balances to return
      // This ensures all leave types are available when switching employees without page refresh

      // Get credit history for annual and sick leaves (reuse if already fetched)
      const creditHistory = hasActualCreditHistory ? creditHistoryCheck : await getEmployeeCreditHistory(employeeId);
      const annualLeaveCredits = creditHistory.filter(c => c.leaveType_Id === 1);
      const sickLeaveCredits = creditHistory.filter(c => c.leaveType_Id === 2);

      // Calculate total credits - parseFloat to ensure numeric calculation
      const annualLeaveTotal = annualLeaveCredits.reduce((sum, credit) =>
        sum + (parseFloat(credit.days_Credited_FullPay) || 0) + (parseFloat(credit.days_Credited_HalfPay) || 0) + (parseFloat(credit.days_Credited_NoPay) || 0), 0);
      const sickLeaveTotal = sickLeaveCredits.reduce((sum, credit) =>
        sum + (parseFloat(credit.days_Credited_FullPay) || 0) + (parseFloat(credit.days_Credited_HalfPay) || 0) + (parseFloat(credit.days_Credited_NoPay) || 0), 0);

      console.log('Credit calculation - Annual Leave credits:', annualLeaveCredits.length, 'Total:', annualLeaveTotal);

      // Get leave types data for gender-based filtering
      const leaveTypesData = await getLeaveTypes({});

      // Filter leave types based on gender
      const applicableLeaveTypes = leaveTypesData.filter(lt => {
        if (lt.leaveType_Id === 3) return employeeGenderId === 5; // Maternity
        if (lt.leaveType_Id === 4) return employeeGenderId === 1; // Paternity
        return true;
      });

      // Get existing balances for current year
      const existingBalances = await getLeaveBalanceHistory({
        Employee_Id: employeeId,
        BalanceYear: currentYear
      });

      const newBalances = [];

      for (const lt of applicableLeaveTypes) {
        const existingBalance = existingBalances ? existingBalances.find(b => b.leaveType_Id === lt.leaveType_Id) : null;

        let totalFullPaid = lt.leaveType_FullPaidDays || 0;
        let totalHalfPaid = lt.leaveType_HalfPaidDays || 0;
        let totalUnpaid = lt.leaveType_UnPaidDays || 0;

        // For Annual Leave, use calculated credits
        if (lt.leaveType_Id === 1) {
          totalFullPaid = annualLeaveTotal;
        }
        // For Sick Leave, always use defaults from leaveTypes (not calculated from credits)
        // The values remain as set from lt.leaveType_FullPaidDays, etc. above

        // For Annual Leave, always use calculated total from credit history (not existing balance)
        // This ensures the balance reflects actual credits even if LeaveBalanceHistories was created with 0
        const finalTotalFullPaid = (lt.leaveType_Id === 1) ? totalFullPaid : (existingBalance ? parseFloat(existingBalance.total_FullPaidDays || 0) : totalFullPaid);
        const finalTotalHalfPaid = existingBalance ? parseFloat(existingBalance.total_HalfPaidDays || 0) : totalHalfPaid;
        const finalTotalUnpaid = existingBalance ? parseFloat(existingBalance.total_UnpaidDays || 0) : totalUnpaid;

        newBalances.push({
          Employee_Id: employeeId,
          LeaveType_Id: lt.leaveType_Id,
          LeaveTypeName: lt.leaveType_NameEn || lt.leaveType_Name,
          BalanceYear: currentYear,
          Total_FullPaidDays: finalTotalFullPaid,
          Total_HalfPaidDays: finalTotalHalfPaid,
          Total_UnpaidDays: finalTotalUnpaid,
          Used_FullPaidDays: existingBalance ? parseFloat(existingBalance.used_FullPaidDays || 0) : 0,
          Used_HalfPaidDays: existingBalance ? parseFloat(existingBalance.used_HalfPaidDays || 0) : 0,
          Used_UnpaidDays: existingBalance ? parseFloat(existingBalance.used_UnpaidDays || 0) : 0,
          LastResetDate: existingBalance ? existingBalance.lastResetDate : new Date().toISOString(),
          Carried_Forward_Days: existingBalance ? parseFloat(existingBalance.carried_Forward_Days || 0) : 0,
          // For Annual Leave, always use calculated total (finalTotalFullPaid) for Current_Year_Balance
          // For other leave types, use existing balance if available
          Current_Year_Balance: (lt.leaveType_Id === 1) ? finalTotalFullPaid : (existingBalance ? parseFloat(existingBalance.current_Year_Balance || 0) : finalTotalFullPaid),
          History_Id: existingBalance ? existingBalance.history_Id : 0
        });
      }

      setLeaveBalances(newBalances);
      return newBalances;
    } catch (error) {
      console.error('Error in initializeBalances:', error);
      throw error;
    }
  };

  const handleEmployeeChange = async (selectedOption) => {
    setSelectedEmployee(selectedOption);
    if (selectedOption) {
      setSelectedEmployeeDetails(selectedOption);

      setLoading(true);
      try {
        const creditHistoryData = await getEmployeeCreditHistory(selectedOption.value);
        setCreditHistory(creditHistoryData);

        // Check if employee has no credit history
        const noCreditHistory = !creditHistoryData || creditHistoryData.length === 0;
        setHasNoCreditHistory(noCreditHistory);

        // Only process credits if employee has existing credit history
        if (!noCreditHistory) {
          await processEmployeeCredits(selectedOption.value);
        }
        await initializeBalances(selectedOption.value);

        // Check for next year data and update available years
        const currentYear = new Date().getFullYear();
        const nextYear = currentYear + 1;
        const nextYearBalances = await getLeaveBalanceHistory({
          Employee_Id: selectedOption.value,
          BalanceYear: nextYear
        });

        if (nextYearBalances && nextYearBalances.length > 0) {
          setAvailableYears([currentYear, nextYear]);
        } else {
          setAvailableYears([currentYear]);
        }
      } catch (error) {
        console.error('Error processing employee credits:', error);

        // Extract error message from API response
        let errorMessage = 'An error occurred while processing employee credits.';

        if (error.response?.data) {
          // If the API returns a specific error message
          errorMessage = error.response.data;
        } else if (error.message) {
          // Use the error message from the exception
          errorMessage = error.message;
        }

        Swal.fire({
          title: 'Error',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedEmployeeDetails(null);
      setLeaveBalances([]);
      setCreditHistory([]);
      setAvailableYears([new Date().getFullYear()]);
      setSelectedYear(new Date().getFullYear());
      setHasNoCreditHistory(false);
    }
  };

  const loadBalancesForYear = async (employeeId, year) => {
    try {
      const currentYear = new Date().getFullYear();

      // Get balances for selected year
      const balances = await getLeaveBalanceHistory({
        Employee_Id: employeeId,
        BalanceYear: year
      });

      // Get Annual and Compensatory leaves from current year (carry-forward)
      const currentYearBalances = await getLeaveBalanceHistory({
        Employee_Id: employeeId,
        BalanceYear: currentYear
      });

      if (balances && balances.length > 0) {
        // Get Annual and Compensatory leaves from current year
        const annualAndCompensatoryBalances = currentYearBalances ?
          currentYearBalances.filter(b => b.leaveType_Id === 1 || b.leaveType_Id === 8) : [];

        // Get other leaves from selected year
        const otherBalances = balances.filter(b => b.leaveType_Id !== 1 && b.leaveType_Id !== 8);

        const formattedBalances = [...annualAndCompensatoryBalances, ...otherBalances].map(balance => {
          const leaveType = leaveTypes.find(lt => lt.value === balance.leaveType_Id);
          return {
            Employee_Id: balance.employee_Id,
            LeaveType_Id: balance.leaveType_Id,
            LeaveTypeName: leaveType ? leaveType.label : `Leave Type ${balance.leaveType_Id}`,
            BalanceYear: balance.balanceYear,
            Total_FullPaidDays: parseFloat(balance.total_FullPaidDays || 0),
            Total_HalfPaidDays: parseFloat(balance.total_HalfPaidDays || 0),
            Total_UnpaidDays: parseFloat(balance.total_UnpaidDays || 0),
            Used_FullPaidDays: parseFloat(balance.used_FullPaidDays || 0),
            Used_HalfPaidDays: parseFloat(balance.used_HalfPaidDays || 0),
            Used_UnpaidDays: parseFloat(balance.used_UnpaidDays || 0),
            LastResetDate: balance.lastResetDate,
            Carried_Forward_Days: parseFloat(balance.carried_Forward_Days || 0),
            Current_Year_Balance: parseFloat(balance.current_Year_Balance || 0),
            History_Id: balance.history_Id
          };
        });

        setLeaveBalances(formattedBalances);
      } else {
        setLeaveBalances([]);
      }
    } catch (error) {
      console.error('Error loading balances for year:', error);
      setLeaveBalances([]);
    }
  };

  const handleYearChange = async (selectedYearOption) => {
    if (selectedEmployee && selectedYearOption) {
      setSelectedYear(selectedYearOption.value);
      await loadBalancesForYear(selectedEmployee.value, selectedYearOption.value);
    }
  };

  const handleBalanceChange = (index, field, value) => {
    console.log('handleBalanceChange called with:', { index, field, value });

    // Handle empty or invalid input
    const numericValue = value === '' || isNaN(value) ? 0 : value;

    const updatedBalances = [...leaveBalances];
    updatedBalances[index] = {
      ...updatedBalances[index],
      [field]: numericValue
    };

    // If Total_FullPaidDays is being updated, also update Current_Year_Balance
    if (field === 'Total_FullPaidDays') {
      updatedBalances[index].Current_Year_Balance = numericValue;
    }

    console.log('Updated balance:', updatedBalances[index]);
    setLeaveBalances(updatedBalances);
  };

  const handleProcessCredit = async () => {
    if (!selectedEmployee) {
      Swal.fire('Warning', 'Please select an employee first', 'warning');
      return;
    }

    // Check for unpaid leave without rejoin date before processing
    try {
      const unpaidLeaves = await getEmployeeLeaves({
        Leave_Employee_Id: selectedEmployee.value,
        Leave_LeaveType_Id: 9  // Unpaid Leave Type
      });

      if (unpaidLeaves.isSuccess && unpaidLeaves.data) {
        const hasUnpaidLeaveWithoutRejoin = unpaidLeaves.data.some(leave =>
          !leave.leave_RejoinDate && !leave.Is_Cancelled && !leave.is_Cancelled
        );

        if (hasUnpaidLeaveWithoutRejoin) {
          Swal.fire({
            title: 'Error',
            text: 'Credit processing blocked: Unpaid leave exists without a rejoin date.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking unpaid leaves:', error);
      // Continue with processing if we can't check unpaid leaves
    }

    setIsProcessing(true);
    try {
      console.log('Current leave balances before save:', leaveBalances);

      // Check if automatic crediting has started (for update validation)
      let automaticCreditingStarted = false;
      if (!hasNoCreditHistory) {
        try {
          const result = await hasAutomaticCreditingStarted(selectedEmployee.value);
          automaticCreditingStarted = result.hasStarted;
        } catch (error) {
          console.error('Error checking automatic crediting status:', error);
        }
      }

      // For employees with no credit history, validate and process credits with adjusted balances first
      if (hasNoCreditHistory) {
        // Show first-time credit alert
        const result = await Swal.fire({
          title: 'First Time Credit Entry',
          text: `This is the first time processing leave credits for ${selectedEmployeeDetails.label}. Initial annual leave balance will be set manually.`,
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: 'Continue',
          cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) {
          setIsProcessing(false);
          return;
        }

        // Get leave balances for processing
        const annualLeave = leaveBalances.find(b => b.LeaveType_Id === 1);
        const sickLeave = leaveBalances.find(b => b.LeaveType_Id === 2);

        const adjustedCredits = {};

        // Extract values from the leave balances table (reuse the same variables)
        const annualLeaveBalance = annualLeave;

        // Determine CreditMode: NEW_EMPLOYEE for new employees (no credit history), INITIAL_MIGRATION for old employees
        // New employees: No credit history AND Annual Leave = 0 → NEW_EMPLOYEE mode
        // Old employees: Has credit history OR Annual Leave > 0 → INITIAL_MIGRATION mode
        const annualLeaveValue = parseFloat(annualLeaveBalance?.Total_FullPaidDays || 0);
        adjustedCredits.CreditMode = (hasNoCreditHistory && annualLeaveValue === 0) ? 'NEW_EMPLOYEE' : 'INITIAL_MIGRATION';

        // Only send annual leave parameter as per updated stored procedure
        if (annualLeaveBalance) {
          adjustedCredits.AdjustedAnnualFullPay = annualLeaveValue;
        }

        await processEmployeeCredits(selectedEmployee.value, adjustedCredits);

        // Refresh credit history after processing
        const updatedCreditHistory = await getEmployeeCreditHistory(selectedEmployee.value);
        setCreditHistory(updatedCreditHistory);

        // Refresh balances to get updated values (procedure updated Annual Leave)
        const updatedBalances = await initializeBalances(selectedEmployee.value);

        // Check if Sick Leave already exists (procedure may have inserted it for new employees)
        const existingBalances = await getLeaveBalanceHistory({
          Employee_Id: selectedEmployee.value,
          BalanceYear: new Date().getFullYear()
        });
        const existingSickLeave = existingBalances?.find(b => b.leaveType_Id === 2);

        // IMPORTANT: Save other leave types (NOT Annual Leave - procedure handles it)
        // Procedure only handles Annual Leave and Sick Leave (for new employees)
        // We need to save Paternity, Parental, Bereavement, Study, Compensatory, Unpaid Leave
        if (updatedBalances && updatedBalances.length > 0) {
          for (const balance of updatedBalances) {
            // Skip Annual Leave (LeaveType_Id = 1) - procedure handles it
            if (balance.LeaveType_Id === 1) {
              console.log('Skipping Annual Leave save - procedure handles it');
              continue;
            }

            // Skip Sick Leave for new employees - procedure already inserted it
            if (hasNoCreditHistory && balance.LeaveType_Id === 2 && existingSickLeave) {
              console.log('Skipping Sick Leave save for new employee - procedure already inserted default 15/30/45');
              continue;
            }

            // Save all other leave types (Paternity, Parental, Bereavement, Study, Compensatory, Unpaid)
            console.log('Saving balance for leave type:', balance.LeaveTypeName, balance);
            await saveLeaveBalanceHistory(balance);
          }
        }
      } else if (automaticCreditingStarted) {
        // Automatic crediting has started, show warning
        Swal.fire('Update Not Allowed', 'Cannot update manual credit. Automatic crediting has already started for this employee.', 'warning');
        setIsProcessing(false);
        return;
      } else {
        // For employees with existing credit history (not first time), save current balances
        for (const balance of leaveBalances) {
          console.log('Saving balance:', balance);
          await saveLeaveBalanceHistory(balance);
        }
      }

      Swal.fire({
        title: 'Success',
        text: 'Leave credits processed successfully',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });

      // Check if next year data needs to be created
      const currentYear = new Date().getFullYear();
      const nextYear = currentYear + 1;

      // Check if next year balances exist
      const nextYearBalances = await getLeaveBalanceHistory({
        Employee_Id: selectedEmployee.value,
        BalanceYear: nextYear
      });

      // If next year doesn't exist, create it for non-carry-forward leaves
      if (!nextYearBalances || nextYearBalances.length === 0) {
        const leaveTypesData = await getLeaveTypes({});
        const nextYearBalancesToCreate = [];

        // Get employee details for gender check
        const employeeData = await getEmployees({ employee_Id: selectedEmployee.value });
        const employeeGenderId = employeeData[0].employee_Gender_Id;

        // Filter leave types based on gender (same logic as current year)
        const applicableLeaveTypes = leaveTypesData.filter(lt => {
          if (lt.leaveType_Id === 3) return employeeGenderId === 5; // Maternity
          if (lt.leaveType_Id === 4) return employeeGenderId === 1; // Paternity
          return true;
        });

        for (const lt of applicableLeaveTypes) {
          // Skip Annual Leave (ID: 1) and Compensatory Leave (ID: 8) - these are carry-forward
          if (lt.leaveType_Id !== 1 && lt.leaveType_Id !== 8) {
            const nextYearBalance = {
              Employee_Id: selectedEmployee.value,
              LeaveType_Id: lt.leaveType_Id,
              LeaveTypeName: lt.leaveType_NameEn,
              BalanceYear: nextYear,
              Total_FullPaidDays: lt.leaveType_FullPaidDays || 0,
              Total_HalfPaidDays: lt.leaveType_HalfPaidDays || 0,
              Total_UnpaidDays: lt.leaveType_UnPaidDays || 0,
              Used_FullPaidDays: 0,
              Used_HalfPaidDays: 0,
              Used_UnpaidDays: 0,
              LastResetDate: new Date().toISOString(),
              Carried_Forward_Days: 0,
              Current_Year_Balance: lt.leaveType_FullPaidDays || 0,
              History_Id: 0
            };
            nextYearBalancesToCreate.push(nextYearBalance);
          }
        }

        // Save next year balances
        for (const balance of nextYearBalancesToCreate) {
          await saveLeaveBalanceHistory(balance);
        }

        // Update available years
        setAvailableYears([currentYear, nextYear]);
      }

      // Get the updated balances for current year
      const updatedBalances = await getLeaveBalanceHistory({
        Employee_Id: selectedEmployee.value,
        BalanceYear: currentYear
      });

      console.log('Updated balances from API:', updatedBalances);

      if (!Array.isArray(updatedBalances)) {
        throw new Error('Invalid response from getLeaveBalanceHistory');
      }

      // Format the balances
      const formattedBalances = updatedBalances.map(balance => {
        const leaveType = leaveTypes.find(lt => lt.value === balance.leaveType_Id);
        return {
          Employee_Id: balance.employee_Id,
          LeaveType_Id: balance.leaveType_Id,
          LeaveTypeName: leaveType ? leaveType.label : `Leave Type ${balance.leaveType_Id}`,
          BalanceYear: balance.balanceYear,
          Total_FullPaidDays: parseFloat(balance.total_FullPaidDays || 0),
          Total_HalfPaidDays: parseFloat(balance.total_HalfPaidDays || 0),
          Total_UnpaidDays: parseFloat(balance.total_UnpaidDays || 0),
          Used_FullPaidDays: parseFloat(balance.used_FullPaidDays || 0),
          Used_HalfPaidDays: parseFloat(balance.used_HalfPaidDays || 0),
          Used_UnpaidDays: parseFloat(balance.used_UnpaidDays || 0),
          LastResetDate: balance.lastResetDate,
          Carried_Forward_Days: parseFloat(balance.carried_Forward_Days || 0),
          Current_Year_Balance: parseFloat(balance.current_Year_Balance || 0),
          History_Id: balance.history_Id
        };
      });

      console.log('Final formatted balances:', formattedBalances);
      setLeaveBalances(formattedBalances);
    } catch (error) {
      console.error('Error processing credits:', error);

      // Extract error message from API response
      let errorMessage = 'Failed to process leave credits.';

      if (error.response?.data) {
        // If the API returns a specific error message
        errorMessage = error.response.data;
      } else if (error.message) {
        // Use the error message from the exception
        errorMessage = error.message;
      }

      Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Employee Leave Credit Management</h4>
                <Form>
                  <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Select Company</Label>
                        <Select
                          value={selectedBusiness}
                          onChange={handleBusinessChange}
                          options={businesses}
                          isSearchable
                          isClearable={!session.businessId}
                          isDisabled={!!session.businessId}
                          placeholder="Select Company"
                          className="react-select"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (provided) => ({
                              ...provided,
                              backgroundColor: '#fff',
                              borderColor: '#ced4da',
                              color: '#495057'
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                              color: state.isSelected ? '#fff' : '#495057',
                              opacity: 1
                            }),
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 9999
                            })
                          }}
                          maxMenuHeight={200}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Select Branch</Label>
                        <Select
                          value={selectedBranch}
                          onChange={handleBranchChange}
                          options={branches}
                          isSearchable
                          isClearable={!session.branchId}
                          isDisabled={!!session.branchId || !selectedBusiness}
                          placeholder={selectedBusiness ? "Select Branch" : "Select Company first"}
                          className="react-select"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (provided) => ({
                              ...provided,
                              backgroundColor: '#fff',
                              borderColor: '#ced4da',
                              color: '#495057',
                              opacity: selectedBusiness ? 1 : 0.6
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                              color: state.isSelected ? '#fff' : '#495057',
                              opacity: 1
                            }),
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 9999
                            })
                          }}
                          maxMenuHeight={200}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label>Select Employee</Label>
                        <Select
                          value={selectedEmployee?.value ? employeeSearchOptions.find(opt => opt.value === selectedEmployee.value) || selectedEmployee : null}
                          onChange={(option) => {
                            handleEmployeeChange(option);
                            if (option) {
                              setEmployeeSearchOptions([]);
                            }
                          }}
                          options={employeeSearchOptions}
                          isSearchable
                          isClearable
                          isLoading={employeeSearchLoading}
                          filterOption={() => true}
                          noOptionsMessage={({ inputValue }) => {
                            if (employeeSearchLoading) return "Searching...";
                            if (!inputValue || inputValue.length < 3) return "Type at least 3 characters to search";
                            return "No employees found";
                          }}
                          loadingMessage={() => "Searching employees..."}
                          onInputChange={(inputValue, { action }) => {
                            if (action === 'input-change') {
                              if (searchTimeout) {
                                clearTimeout(searchTimeout);
                              }
                              const timeout = setTimeout(() => {
                                searchEmployeesManually(inputValue);
                              }, 300);
                              setSearchTimeout(timeout);
                            }
                            return inputValue;
                          }}
                          placeholder="Type at least 3 characters to search..."
                          className="react-select"
                          classNamePrefix="select"
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (provided) => ({
                              ...provided,
                              backgroundColor: '#fff',
                              borderColor: '#ced4da',
                              color: '#495057'
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: '#495057'
                            }),
                            option: (provided, state) => ({
                              ...provided,
                              backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                              color: state.isSelected ? '#fff' : '#495057',
                              opacity: 1
                            }),
                            menu: (provided) => ({
                              ...provided,
                              zIndex: 9999
                            })
                          }}
                          maxMenuHeight={200}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {selectedEmployeeDetails && (
                    <Row className="mt-3">
                      <Col md={4}>
                        <FormGroup>
                          <Label><strong>Employee Code:</strong></Label>
                          <div className="form-control-plaintext">{selectedEmployeeDetails.employeeCode}</div>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label><strong>Join Date:</strong></Label>
                          <div className="form-control-plaintext">
                            {selectedEmployeeDetails.joinDate ? new Date(selectedEmployeeDetails.joinDate).toLocaleDateString('en-GB') : 'N/A'}
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label><strong>Select Year:</strong></Label>
                          <Select
                            value={{ value: selectedYear, label: selectedYear.toString() }}
                            onChange={handleYearChange}
                            options={availableYears.map(year => ({ value: year, label: year.toString() }))}
                            isSearchable={false}
                            placeholder="Select Year"
                            className="react-select"
                            classNamePrefix="select"
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (provided) => ({
                                ...provided,
                                backgroundColor: '#fff',
                                borderColor: '#ced4da',
                                color: '#495057'
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                color: '#495057'
                              }),
                              option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                                color: state.isSelected ? '#fff' : '#495057',
                                opacity: 1
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999
                              })
                            }}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  {hasNoCreditHistory && (
                    <Row className="mt-3">
                      <Col xs={12}>
                        <div className="alert alert-warning">
                          <strong>First Time Credit Entry:</strong> This employee has no leave credit history.
                          Please process their leave credits to establish baseline credit history.
                        </div>
                      </Col>
                    </Row>
                  )}

                  {loading ? (
                    <div className="text-center mt-4">
                      <ClipLoader size={40} color="#123abc" loading={true} />
                    </div>
                  ) : (
                    <>
                      {selectedEmployee && (
                        <>
                          <Row className="mt-4">
                            <Col xs={12}>
                              <h5>Leave Balances</h5>
                              <div className="table-responsive">
                                <Table className="table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Leave Type</th>
                                      <th>Full Paid Days</th>
                                      <th>Used Full Paid</th>
                                      <th>Half Paid Days</th>
                                      <th>Used Half Paid</th>
                                      <th>Unpaid Days</th>
                                      <th>Used Unpaid</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {leaveBalances.map((balance, index) => (
                                      <tr key={`balance-${balance.LeaveType_Id || index}`}>
                                        <td>{balance.LeaveTypeName}</td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Total_FullPaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Total_FullPaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Used_FullPaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Used_FullPaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Total_HalfPaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Total_HalfPaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Used_HalfPaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Used_HalfPaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Total_UnpaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Total_UnpaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                        <td>
                                          <Input
                                            type="number"
                                            value={balance.Used_UnpaidDays || 0}
                                            onChange={(e) => handleBalanceChange(index, 'Used_UnpaidDays', parseFloat(e.target.value || 0))}
                                            step="0.5"
                                            min="0"
                                            disabled={isViewOnly}
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mt-4">
                            <Col xs={12}>
                              <h5>Credit History</h5>
                              <div className="table-responsive">
                                <Table className="table-bordered mb-0">
                                  <thead>
                                    <tr>
                                      <th>Credit Date</th>
                                      <th>Leave Type</th>
                                      <th>Credit Type</th>
                                      <th>Full Paid Days</th>
                                      <th>Half Paid Days</th>
                                      <th>Unpaid Days</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {creditHistory
                                      .filter(credit => credit.leaveType_Id === 1) // Only show Annual Leave credit history
                                      .map((credit, index) => {
                                        const leaveType = leaveTypes.find(lt => lt.value === credit.leaveType_Id);
                                        return (
                                          <tr key={`credit-${index}`}>
                                            <td>{new Date(credit.credit_Date).toLocaleDateString('en-GB')}</td>
                                            <td>{leaveType?.label || `Leave Type ${credit.leaveType_Id}`}</td>
                                            <td>{credit.credit_Type}</td>
                                            <td>{credit.days_Credited_FullPay}</td>
                                            <td>{credit.days_Credited_HalfPay}</td>
                                            <td>{credit.days_Credited_NoPay}</td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </Table>
                              </div>
                            </Col>
                          </Row>

                          {hasEditOrHigher && (
                            <Row className="mt-4">
                              <Col xs={12} className="text-end">
                                <Button
                                  color="primary"
                                  onClick={handleProcessCredit}
                                  disabled={isProcessing}
                                >
                                  {isProcessing ? (
                                    <>
                                      <ClipLoader size={20} color="#ffffff" loading={true} />
                                      <span className="ms-2">Processing...</span>
                                    </>
                                  ) : (
                                    'Process Credit'
                                  )}
                                </Button>
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EmployeeLeaveCreditPage; 